import React from 'react';

/**
 * Read-only control overview for the Test Plan tab.
 * Small fields are packed multiple-per-row in a grid; only Control Wording and
 * Objective span the full width. Avoids the tall column of blank space.
 */
export default function OverviewReadOnly({ control, linkedRisks = [], linkedRisksLoading = false }) {
  if (!control) return null;

  const dataSourceLabel =
    control.data_source_type === 'upload' ? 'File Upload'
      : control.data_source_type === 'directory' ? 'Directory Sync'
      : control.data_source_type === 'integration' ? 'System Integration'
      : 'Not Configured';

  // Compact fields — rendered in a responsive grid, several per row.
  const compact = [
    { label: 'Control ID',                 value: control.control_id },
    { label: 'Control Type',               value: control.control_type },
    { label: 'Frequency',                  value: control.frequency },
    { label: 'Significance',               value: control.significance },
    { label: 'Nature',                     value: control.control_nature },
    { label: 'Risk Level',                 value: control.control_risk_level },
    { label: 'SOX Process',                value: control.sox_process },
    { label: 'Sub-process',                value: control.sub_process },
    { label: 'Control Performer',          value: control.control_performer },
    { label: 'MRC Control',                value: control.mrc_control ? 'Yes' : 'No' },
    { label: 'Data Source',                value: dataSourceLabel },
    { label: 'FY External Audit Reliance', value: control.sampling_attributes?.reliance_strategy },
    { label: 'Assertions',                 value: (control.assertions || []).join(', ') },
    { label: 'Last Updated',               value: control.last_edited ? new Date(control.last_edited).toLocaleDateString() : null },
  ];

  const Title = control.title;
  const wording = control.control_wording_text || control.control_wording;

  return (
    <div className="p-6 space-y-5">
      {/* Title — full width */}
      {Title && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">Title</p>
          <p className="text-sm text-gray-900 font-medium">{Title}</p>
        </div>
      )}

      {/* Compact fields grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
        {compact.map(({ label, value }) =>
          value ? (
            <div key={label}>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
              <p className="text-sm text-gray-900 break-words capitalize">{value}</p>
            </div>
          ) : null
        )}
      </div>

      {/* Control Wording — full width */}
      {wording && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Control Wording</p>
          <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{wording}</p>
        </div>
      )}

      {/* Objective — full width */}
      {control.objective && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Objective</p>
          <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{control.objective}</p>
        </div>
      )}

      {/* Risk Mitigated — full width */}
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Risk Mitigated</p>
        {linkedRisksLoading ? (
          <span className="text-sm text-gray-400">Loading…</span>
        ) : (linkedRisks || []).length === 0 ? (
          <span className="text-sm text-gray-400 italic">No risks linked yet.</span>
        ) : (
          <ul className="space-y-2">
            {linkedRisks.map((r, i) => (
              <li key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-gray-800">
                {r.risk_description || r.riskDescription || r.description}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
